import React, { useState, useEffect, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import { isArray } from 'lodash';
import { RootState } from 'store';

import FiltersBottomSheet, { FilterItem, PriceFilter } from 'components/FiltersBottomSheet';
import Select from 'components/FiltersBottomSheet/components/Select';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import useIsDesktop from 'hooks/useIsDesktop';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { Airport } from 'models/airport';
import { VehicleFilterResponse, VehicleFilter, FilterSorter, FilterPriceRange } from 'models/auto/vehicleFilter';
import { VehicleResultsFilters, VehicleSorterTypes } from 'models/auto/vehicleResults';
import { VehiclePlace } from 'models/auto/vehicleSearch';
import { City } from 'models/city';
import { amplitudelogEvent, FilterGroupEvent, SelectFilterEvent } from 'services/amplitudeService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';

import LoaderDesktop from './components/LoaderDesktop';
import Order from './components/Order';
import TravelPolicies from 'components/FlightsFilters/components/TravelPolicies';

export interface Props {
  show: boolean;
  defaultFilters?: VehicleResultsFilters;
  availableFilters?: VehicleFilterResponse;
  sorter?: FilterSorter;
  days?: number;
  onSearch: (payload: VehicleResultsFilters) => void;
  onClose: () => void;
  currency: string;
  isLoading: boolean;
  pickUpPoint?: Airport | City | VehiclePlace;
  dropOffPoint?: Airport | City | VehiclePlace;
  pickUpDate?: Date;
  dropOffDate?: Date;
}

export const DEFAULT_ORDER = VehicleSorterTypes.Recommended;
export const DEFAULT_ORDER_DESC = false;
export const PROMO = 'PROMO';
const TIMEOUT_INTERVAL = 700;

const mapStringToNumberArr = (data: string[]): number[] => data.map((value) => Number(value));
const mapStringToBooleanArr = (data: string[]): boolean[] => data.map((value) => value === 'true');
const mapNumberToStringArr = (data: number[]): string[] => data.map((value) => `${value}`);
const mapBooleanToStringArr = (data: boolean[]): string[] => data.map((value) => String(value).toLowerCase());
const mapPriceRangeToArray = (data?: FilterPriceRange[]): number[] => {
  if (data && data.length > 0) {
    const lower = data.first() && data.first().upperBound ? data.first().upperBound : 0;
    const higher = data.last() && data.last().lowerBound ? data.last().lowerBound : 0;
    return lower && higher ? [lower, higher] : [0, 0];
  }
  return [0, 0];
};

export const AutosFilters = ({
  defaultFilters,
  availableFilters,
  sorter,
  days = 1,
  onClose,
  onSearch,
  currency,
  isLoading,
  pickUpPoint,
  dropOffPoint,
  pickUpDate,
  dropOffDate,
  ...props
}: Props) => {
  const { t } = useStoreTranslation();
  const isDesktop = useIsDesktop();
  const user = useUserAccount();

  const [orderCriteria, setOrderCriteria] = useState<VehicleSorterTypes>(
    sorter?.orderCriteria ? sorter?.orderCriteria : DEFAULT_ORDER,
  );
  const [orderDesc, setOrderDesc] = useState<boolean>(
    sorter && typeof sorter.orderDesc !== 'undefined' ? sorter?.orderDesc : DEFAULT_ORDER_DESC,
  );
  const [orderType, setOrderType] = useState<VehicleSorterTypes>(
    sorter?.orderCriteria === VehicleSorterTypes.Price && sorter?.orderDesc === true
      ? VehicleSorterTypes.PriceDesc
      : sorter?.orderCriteria || DEFAULT_ORDER,
  );
  const [company, setCompany] = useState<string[]>(defaultFilters?.company ? defaultFilters?.company : []);
  const [transmission, setTransmission] = useState<string[]>(
    defaultFilters?.transmissionType ? defaultFilters?.transmissionType : [],
  );
  const [maxPax, setMaxPax] = useState<number[]>(defaultFilters?.maxPax ? defaultFilters?.maxPax : []);
  const [doors, setDoors] = useState<number[]>(defaultFilters?.doors ? defaultFilters?.doors : []);
  const [model, setModel] = useState<string[]>(defaultFilters?.model ? defaultFilters?.model : []);
  const [fuelType, setFuelType] = useState<string[]>(defaultFilters?.fuelType ? defaultFilters?.fuelType : []);
  const [airConditioned, setAirConditioned] = useState<boolean[]>(
    defaultFilters?.airConditioned ? defaultFilters?.airConditioned : [],
  );
  const [category, setCategory] = useState<string[]>(
    defaultFilters?.vehicleCategory ? defaultFilters?.vehicleCategory : [],
  );
  const [price, setPrice] = useState<number[]>(defaultFilters?.priceRanges || []);
  const [minMax, setMinMax] = useState<number[]>(defaultFilters?.priceRanges || []);

  const [travelPolicies, setTravelPolicies] = useState<string[]>(defaultFilters?.travelPolicies || []);
  const [filtersCount, setFiltersCount] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState<number>();
  const [changed, setChanged] = useState(false);

  const search = useSelector((state: RootState) => state.autos.search);

  const sendAmplitudeEvent = (filterGroup: FilterGroupEvent, filterValue: any) => {
    const amplitudeSelectFilter: SelectFilterEvent = {
      FILTER_GROUP: filterGroup,
      FILTER_VALUE: isArray(filterValue) ? filterValue : filterValue.toString(),
      TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
      TRAVEL_FROM: pickUpPoint?.code ?? '',
      TRAVEL_TO: dropOffPoint?.code ?? '',
      TRAVEL_DESTINATION_TYPE:
        search?.distanceRestriction && search?.distanceRestriction === 'Domestic' ? 'NATIONAL' : 'INTERNATIONAL',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), pickUpDate).toString(),
      TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
      TRAVEL_DATE_TO: dropOffDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH: daysCount(pickUpDate, dropOffDate).toString(),
      TRAVEL_PASSENGERS: '1',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: search?.searchId ?? '',
    };

    amplitudelogEvent(amplitudeEvents.SELECT_FILTER, amplitudeSelectFilter, user);
  };

  useEffect(() => {
    if (
      availableFilters &&
      availableFilters.filter &&
      availableFilters.filter.priceRanges &&
      availableFilters.filter.priceRanges.length > 1
    ) {
      const priceRange = mapPriceRangeToArray(availableFilters.filter.priceRanges);
      setPrice(priceRange);
      setMinMax(priceRange);
    }
  }, [availableFilters]);

  useEffect(() => {
    let count = 0;
    if (category && category.length > 0) {
      count += category.length;
    }
    if (transmission && transmission.length > 0) count += transmission.length;
    if (maxPax && maxPax.length > 0) count += maxPax.length;
    if (doors && doors.length > 0) count += doors.length;
    if (fuelType && fuelType.length > 0) count += fuelType.length;
    if (airConditioned && airConditioned.length > 0) count += airConditioned.length;
    if (company && company.length > 0) count += company.length;
    if (model && model.length > 0) count += model.length;

    if (price && minMax && (price.first() !== minMax.first() || price.last() !== minMax.last())) {
      count++;
    }

    setFiltersCount(count);
  }, [
    airConditioned,
    category,
    company,
    doors,
    fuelType,
    maxPax,
    minMax,
    model,
    orderCriteria,
    orderDesc,
    price,
    transmission,
    travelPolicies,
  ]);

  const setOrder = (orderCriteria: VehicleSorterTypes) => {
    setOrderType(orderCriteria);
    setOrderCriteria(orderCriteria === VehicleSorterTypes.PriceDesc ? VehicleSorterTypes.Price : orderCriteria);
    setOrderDesc(orderCriteria === VehicleSorterTypes.PriceDesc ? true : false);
  };

  const handleSearch = useCallback(() => {
    const filter: VehicleResultsFilters = {};
    if (category.length > 0) filter.vehicleCategory = category;
    if (transmission.length > 0) filter.transmissionType = transmission;
    if (maxPax.length > 0) filter.maxPax = maxPax;
    if (doors.length > 0) filter.doors = doors;
    if (fuelType.length > 0) filter.fuelType = fuelType;
    if (airConditioned.length > 0) filter.airConditioned = airConditioned;
    if (company.length > 0) filter.company = company;
    if (model.length > 0) filter.model = model;
    if(travelPolicies.length > 0) filter.travelPolicies = travelPolicies;
    if (price.first() !== minMax.first() || price.last() !== minMax.last()) {
      filter.priceFrom = price.first();
      filter.priceTo = price.last();
    }
    filter.orderCriteria = orderCriteria;
    filter.orderDesc = orderDesc;

    onSearch(filter);
  }, [
    airConditioned,
    category,
    company,
    doors,
    fuelType,
    maxPax,
    minMax,
    model,
    onSearch,
    orderCriteria,
    orderDesc,
    price,
    transmission,
    travelPolicies,
  ]);

  const handleClearFilters = useCallback(() => {
    let priceRange = minMax;
    if (
      availableFilters &&
      availableFilters.filter &&
      availableFilters.filter.priceRanges &&
      availableFilters.filter.priceRanges.length > 1
    ) {
      priceRange = mapPriceRangeToArray(availableFilters.filter.priceRanges);
    }
    setOrderCriteria(DEFAULT_ORDER);
    setOrderDesc(DEFAULT_ORDER_DESC);
    setCompany([]);
    setTransmission([]);
    setMaxPax([]);
    setDoors([]);
    setModel([]);
    setFuelType([]);
    setAirConditioned([]);
    setCategory([]);
    setPrice(priceRange);
    setTravelPolicies([]);
  }, [availableFilters, minMax]);

  useEffect(() => {
    if (typeof defaultFilters === 'undefined') {
      handleClearFilters();
      return;
    }
    setCategory(defaultFilters?.vehicleCategory ? defaultFilters.vehicleCategory : []);
    setTransmission(defaultFilters?.transmissionType ? defaultFilters.transmissionType : []);
    setMaxPax(defaultFilters?.maxPax ? defaultFilters.maxPax : []);
    setDoors(defaultFilters?.doors ? defaultFilters.doors : []);
    setFuelType(defaultFilters?.fuelType ? defaultFilters.fuelType : []);
    setAirConditioned(defaultFilters?.airConditioned ? defaultFilters.airConditioned : []);
    setCompany(defaultFilters?.company ? defaultFilters.company : []);
    setTravelPolicies(defaultFilters?.travelPolicies ? defaultFilters.travelPolicies : []);
    setModel(defaultFilters?.model ? defaultFilters.model : []);
    setPrice(
      defaultFilters?.priceFrom && defaultFilters?.priceTo
        ? [defaultFilters.priceFrom, defaultFilters.priceTo]
        : minMax,
    );
    setOrderCriteria(defaultFilters?.orderCriteria ? defaultFilters.orderCriteria : DEFAULT_ORDER);
    setOrderDesc(
      defaultFilters && typeof defaultFilters.orderDesc !== 'undefined' ? defaultFilters.orderDesc : DEFAULT_ORDER_DESC,
    );
  }, [defaultFilters, handleClearFilters, minMax]);

  const handleSearchPrice = (values: number[]) => {
    setPrice(values);
    let timeoutId = searchTimeout;
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      if (isDesktop) setChanged(true);
      if (values.length > 0) sendAmplitudeEvent(FilterGroupEvent.PRICE, values);
    }, TIMEOUT_INTERVAL);
    setSearchTimeout(timeoutId);
  };

  useEffect(() => {
    if (isDesktop && changed) {
      handleSearch();
      setChanged(false);
    }
  }, [changed, handleSearch, isDesktop]);

  const handleOrderChange = (value) => {
    if (orderType !== value) sendAmplitudeEvent(FilterGroupEvent.ORDER, value);
    setOrder(value);
    if (isDesktop) setChanged(true);
  };

  const filterTravelPolicies = [
    {
      'text': 'withinPolicies'
    },
    {
      'text': 'outOfPolicies'
    }
  ]

  const renderContent = (filters: VehicleFilter) => (
    <>
      <FilterItem
        name={t('autosFilter.orderBy')}
        changed={orderCriteria !== DEFAULT_ORDER || orderDesc !== DEFAULT_ORDER_DESC}
        isOpen={isDesktop}
      >
        <Order
          value={
            orderCriteria === VehicleSorterTypes.Price && orderDesc === true
              ? VehicleSorterTypes.PriceDesc
              : orderCriteria
          }
          onChange={handleOrderChange}
        />
      </FilterItem>

      <FilterItem
        name={t('flightsFilters.travelPolicies.title')}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <TravelPolicies
          values={travelPolicies}
          onChange={(value) => {
            setTravelPolicies(value);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.TRAVELPOLICIES, value);
          }}
          travelPolicies={filterTravelPolicies}
        />
      </FilterItem>
      
      <FilterItem
        name={days === 1 ? t('autosFilter.priceDay') : t('autosFilter.priceDays', { days })}
        changed={price.first() !== minMax.first() || price.last() !== minMax.last()}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <PriceFilter
          min={minMax.first()}
          max={minMax.last()}
          value={price}
          onChange={handleSearchPrice}
          currency={currency}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.vehicleCategory')}
        count={category.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.vehicleCategory.map((category) => ({
              text: `${category.text}`,
              count: category.count ? category.count : 0,
            })) || []
          }
          values={category}
          onChange={(values) => {
            setCategory(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.VEHICLE_CATEGORY, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.transmissionType')}
        count={transmission.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.transmissionType.map((transmission) => ({
              label: t(`autosCard.category.transmission.${transmission.text?.toUpperCase()}`),
              text: `${transmission.text}`,
              count: transmission.count ? transmission.count : 0,
            })) || []
          }
          values={transmission}
          onChange={(values) => {
            setTransmission(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.TRANSMISSION_TYPE, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.company')}
        count={company.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.company
              .filter((c) => c.text?.toUpperCase() !== PROMO)
              .map((c) => ({
                text: `${c.text}`,
                count: c.count ? c.count : 0,
              })) || []
          }
          values={company}
          onChange={(value) => {
            setCompany(value);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.COMPANY, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.maxPax')}
        count={maxPax.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.maxPax.map((p) => ({
              text: `${p.text}`,
              count: p.count ? p.count : 0,
            })) || []
          }
          values={mapNumberToStringArr(maxPax)}
          onChange={(value) => {
            setMaxPax(mapStringToNumberArr(value));
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.MAX_PAX, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.doors')}
        count={doors.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.doors.map((d) => ({
              text: `${d.text}`,
              count: d.count ? d.count : 0,
            })) || []
          }
          values={mapNumberToStringArr(doors)}
          onChange={(value) => {
            setDoors(mapStringToNumberArr(value));
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.DOORS, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.airConditioned')}
        count={airConditioned.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.airConditioned.map((a) => ({
              label: t(`autosDetails.airConditioned.${a.text?.toLowerCase()}`),
              text: `${a.text?.toLowerCase()}`,
              count: a.count ? a.count : 0,
            })) || []
          }
          values={mapBooleanToStringArr(airConditioned)}
          onChange={(value) => {
            setAirConditioned(mapStringToBooleanArr(value));
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.AIR_CONDITIONED, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.model')}
        count={model.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.model.map((m) => ({
              text: `${m.text}`,
              count: m.count ? m.count : 0,
            })) || []
          }
          values={model}
          onChange={(values) => {
            setModel(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.MODEL, value);
          }}
        />
      </FilterItem>

      <FilterItem
        name={t('autosFilter.fuelType')}
        count={fuelType.length}
        isOpen={isDesktop}
        className={isDesktop ? 'no-border' : ''}
      >
        <Select
          options={
            filters?.fuelType.map((f) => ({
              label: f.text === 'Unspecified' ? t('autosDetails.fuelType.unspecified') : `${f.text}`,
              text: `${f.text}`,
              count: f.count ? f.count : 0,
            })) || []
          }
          values={fuelType}
          onChange={(values) => {
            setFuelType(values);
            if (isDesktop) setChanged(true);
          }}
          onOptionChange={(value, isChecked) => {
            if (isChecked) sendAmplitudeEvent(FilterGroupEvent.FUEL_TYPE, value);
          }}
        />
      </FilterItem>
    </>
  );

  return (
    <>
      <Mobile>
        <FiltersBottomSheet
          {...props}
          showSearchButton={Boolean(availableFilters)}
          onClose={onClose}
          onSearch={handleSearch}
          filtersCount={filtersCount}
          onClearFilter={handleClearFilters}
        >
          {!availableFilters ? <Loader fullScreen /> : renderContent(availableFilters.filter)}
        </FiltersBottomSheet>
      </Mobile>
      <Desktop>{isLoading ? <LoaderDesktop /> : availableFilters && renderContent(availableFilters.filter)}</Desktop>
    </>
  );
};

export default memo(AutosFilters);
